/** Pure helpers for the open-world floor (spec §3). No canvas. */
import type { Pillar, Vec2 } from '../../../sim/types.js'

export const FOG_NEAR = 35
export const FOG_FAR = 80
export const GRID_MINOR = 2
export const GRID_MAJOR = 10
export const CALLOUT_STEP = 50
export const CALLOUT_RADIUS = 40
export const MAX_GRID_LINES = 100
export const MAX_PILLARS = 40

/** 0 = clear, 1 = paper. Linear in camera depth. */
export function fog(depth: number, near = FOG_NEAR, far = FOG_FAR): number {
  if (depth <= near) return 0
  if (depth >= far) return 1
  return (depth - near) / (far - near)
}

export interface GridLine { axis: 'x' | 'z'; at: number; major: boolean }

/** Grid lines (x = const and z = const) within `far` of (cx, cz), at `step` spacing, at most `cap` per axis. */
export function gridLines(cx: number, cz: number, far: number, step = GRID_MINOR, cap = MAX_GRID_LINES): GridLine[] {
  const out: GridLine[] = []
  for (const axis of ['x', 'z'] as const) {
    const c = axis === 'x' ? cx : cz
    const lo = Math.ceil((c - far) / step) * step, hi = Math.floor((c + far) / step) * step
    let n = 0
    for (let at = lo; at <= hi && n < cap; at += step, n++) {
      const v = Math.round(at / step) * step
      out.push({ axis, at: v, major: Math.abs(v % GRID_MAJOR) < 1e-9 })
    }
  }
  return out
}

export function cullPillars(pillars: readonly Pillar[], cx: number, cz: number, far: number, cap = MAX_PILLARS): Pillar[] {
  const far2 = far * far
  return pillars
    .map((p) => ({ p, d2: (p.x - cx) ** 2 + (p.z - cz) ** 2 }))
    .filter((e) => e.d2 <= far2)
    .sort((a, b) => a.d2 - b.d2)
    .slice(0, cap)
    .map((e) => e.p)
}

const pad3 = (n: number): string => `${n < 0 ? '−' : '+'}${String(Math.abs(Math.round(n))).padStart(3, '0')}`

/** Survey callout: signed metres from the terminal. */
export function coordLabel(x: number, z: number, terminal: Vec2): string {
  return `X${pad3(x - terminal.x)} Z${pad3(z - terminal.z)}`
}

export function callouts(cx: number, cz: number, terminal: Vec2, step = CALLOUT_STEP, radius = CALLOUT_RADIUS): { x: number; z: number; text: string }[] {
  const out: { x: number; z: number; text: string }[] = []
  for (let x = Math.ceil((cx - radius) / step) * step; x <= cx + radius; x += step)
    for (let z = Math.ceil((cz - radius) / step) * step; z <= cz + radius; z += step)
      if (Math.hypot(x - cx, z - cz) <= radius) out.push({ x: x + 0, z: z + 0, text: coordLabel(x, z, terminal) }) // + 0 normalises −0 from Math.ceil
  return out
}
